import { RouteComponentProps } from "react-router-dom";
import { useEngagementQuery } from "../../../../query/engagements/queries/useEngagementQuery";
import {
  toWSDate,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPill,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSQueries } from "../../../../query/WSQuery";
import { PageHeader } from "../../../../shared/components/PageHeader";
import { EngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import { EngagementInfoPanel } from "../../../Payers/components/EngagementInfoPanel";
import { RequirementsTable } from "./RequirementsTable";
import { useEditEngagementModal } from "../../modals/EditEngagementModal";
import { useAddRequirementToEngagementModal } from "../../../Payees/components/AddRequirementToEngagementModal";
import { RecentContractorsWidget } from "./RecentContractorsWidget";
import { useAddPayeeToEngagementModal } from "../../../Payees/components/AddPayeeToEngagementModal/useAddPayeeToEngagementModal";
import { useEngagementTypeDefaultActions } from "../../hooks/useEngagementTypeDefaultActions";
import {
  getSectionPathByEngagementStatus,
  useGoToEngagementTypes
} from "../../paths";
import { EngagementTypeHistoryWidget } from "./useEngagementTypeHistoryWidget";

export const RouteEngagementTypeDetails: React.FC<
  RouteComponentProps<{
    engagementTypeId: string;
  }>
> = ({
  match: {
    params: { engagementTypeId }
  },
  history,
  location
}) => {
  const goToEngagementTypes = useGoToEngagementTypes();
  const queryEngagement = useEngagementQuery(engagementTypeId);
  const editEngagementModal = useEditEngagementModal();
  const addRequirementToEngagementModal = useAddRequirementToEngagementModal();
  const addPayeeToEngagementModal = useAddPayeeToEngagementModal();
  const fetchDefaultActions = useEngagementTypeDefaultActions();

  return (
    <WSPage>
      <WSQueries queries={{ queryEngagement }}>
        {({ queryEngagementData: engagement }) => {
          return (
            <>
              <PageHeader
                title="Engagements"
                rootPath={getSectionPathByEngagementStatus(engagement.status)}
                breadcrumbs={[
                  {
                    label: "Engagement types",
                    onClick: () => {
                      goToEngagementTypes(engagement.status);
                    }
                  },
                  {
                    label: engagement.name
                  }
                ]}
                fetchMenuItems={() =>
                  fetchDefaultActions({
                    engagement,
                    onDelete: engagement => {
                      goToEngagementTypes(engagement.status);
                    }
                  })
                }
                menuItems={[
                  {
                    icon: "edit-box",
                    label: "Edit information",
                    onClick: () => {
                      editEngagementModal.open({ engagement });
                    }
                  },
                  {
                    icon: "clipboard-check",
                    label: "Add requirement",
                    onClick: () => {
                      addRequirementToEngagementModal.open({
                        requirementDefinitionIds:
                          engagement.requirementDefinitionIds,
                        engagementId: engagement.engagementId
                      });
                    }
                  },
                  {
                    icon: "user",
                    label: "Add contractor",
                    onClick: () => {
                      addPayeeToEngagementModal.open({
                        engagementId: engagement.engagementId
                      });
                    }
                  }
                ]}
              />
              <WSFlexBox.CenterY mt="L" mb="2XL" gap="M">
                <WSText.Heading3 singleLine>{engagement.name}</WSText.Heading3>
                {engagement.status === EngagementStatus.Inactive ? (
                  <WSPill theme="neutral" text="Archived" icon={false} />
                ) : engagement.status === EngagementStatus.Active ? (
                  <WSPill theme="success" text="Active" icon={true} />
                ) : null}
                <WSText.ParagraphXs color="gray500">
                  Last modified {toWSDate(engagement.updatedAt).formattedValue}
                </WSText.ParagraphXs>
              </WSFlexBox.CenterY>
              <WSGrid gutter="2XL">
                <WSGrid.Item span={{ m: "8" }}>
                  {/* Engagement information */}
                  <WSSectionToolbar
                    mb="M"
                    title="Engagement type information"
                    button={{
                      label: "Edit",
                      onClick: () => {
                        editEngagementModal.open({ engagement });
                      }
                    }}
                  />
                  <EngagementInfoPanel
                    name={engagement.name}
                    description={engagement.description}
                    mb="XL"
                  />

                  {/* Requirements */}
                  <WSSectionToolbar
                    mb="M"
                    title="Requirements"
                    pill={{
                      theme: "neutral",
                      outline: true,
                      text: `${
                        engagement.requirementDefinitionIds?.length ?? 0
                      }`
                    }}
                    button={{
                      label: "Add requirement",
                      onClick: () => {
                        addRequirementToEngagementModal.open({
                          requirementDefinitionIds:
                            engagement.requirementDefinitionIds,
                          engagementId: engagement.engagementId
                        });
                      }
                    }}
                  />
                  <RequirementsTable engagement={engagement} />
                </WSGrid.Item>

                <WSGrid.Item span={{ m: "4" }}>
                  <WSList gap="L">
                    <RecentContractorsWidget
                      engagementId={engagement.engagementId}
                    />
                    <EngagementTypeHistoryWidget
                      engagementTypeId={engagementTypeId}
                    />
                    {/*
                  <WSDivider my="XL" />
                  <WSSectionToolbar title="Recent payables" />
                  <RecentPayablesTable payables={[]} /> */}
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
